import { MapPin, Star, ArrowRight } from "lucide-react";
import { Link } from "@/lib/react-router-dom-shim";
import { useTranslation } from "react-i18next";
import { getMobileOptimizedUrl } from "@/utils/imageOptimization";

interface DestinationCardProps {
  name: string;
  location: string;
  image: string;
  description: string;
  rating?: number;
  link: string;
}

export const DestinationCard = ({
  name,
  location,
  image,
  description,
  rating = 4.8,
  link,
}: DestinationCardProps) => {
  const { t } = useTranslation();
  
  return (
    <Link 
      to={link} 
      className="group block"
    >
      <article className="overflow-hidden rounded-lg bg-card transition-all duration-300 hover:shadow-elegant-hover">
        {/* Image Container */}
        <div className="relative aspect-[4/3] overflow-hidden">
          <img
            src={getMobileOptimizedUrl(image, 400, 60)}
            srcSet={`${getMobileOptimizedUrl(image, 320, 60)} 320w, ${getMobileOptimizedUrl(image, 400, 60)} 400w, ${getMobileOptimizedUrl(image, 600, 50)} 600w, ${getMobileOptimizedUrl(image, 800, 40)} 800w`}
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            alt={`${name}, ${location}`}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
            loading="lazy"
            decoding="async"
            width={400}
            height={300}
          />
          
          {/* Rating Badge */}
          <div className="absolute right-4 top-4 flex items-center gap-1 rounded-full bg-card/90 px-3 py-1.5 text-sm font-medium backdrop-blur-sm">
            <Star className="h-4 w-4 fill-secondary text-secondary" aria-hidden="true" />
            <span>{rating}</span>
          </div>
          
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
        </div>
        
        {/* Content */}
        <div className="p-5">
          {/* Location */}
          <div className="mb-2 flex items-center gap-1.5 text-muted-foreground">
            <MapPin className="h-4 w-4" aria-hidden="true" />
            <span className="text-sm">{location}</span>
          </div>
          
          {/* Title */}
          <h3 className="mb-2 font-serif text-xl font-bold text-foreground transition-colors group-hover:text-primary">
            {name}
          </h3>
          
          {/* Description */}
          <p className="mb-4 line-clamp-2 text-sm text-muted-foreground">
            {description}
          </p>
          
          {/* Link */}
          <div className="flex items-center gap-2 text-sm font-semibold text-primary transition-all group-hover:gap-3">
            <span>{t('common.discover')}</span>
            <ArrowRight className="h-4 w-4" />
          </div>
        </div>
      </article>
    </Link>
  );
};
